import { Badge, SimpleGrid, Stack, Text } from "@mantine/core";
import { Alert, Card, spacePx } from "@xplatform/design-system";
import type { DataTableFooterCell } from "@xplatform/design-system";
import { IconCurrencyDollar } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ExternalRefCapabilityUsageViewModel } from "@models/monitoring";
import { AdminTable, col } from "@components/AdminTable";
import { formatCount, formatMoney } from "@utils/monitoringFormat";
import { getCostPercent } from "@utils/formatters";

interface CostBreakdownSectionProps {
  capabilityUsage: ExternalRefCapabilityUsageViewModel[];
}

const sum = (values: Array<number | null>) => values.reduce<number>((total, v) => total + (v ?? 0), 0);

/** Headline totals and the cost per capability of one process or job. */
export const CostBreakdownSection = ({ capabilityUsage }: CostBreakdownSectionProps) => {
  const { t } = useTranslation();

  if (capabilityUsage.length === 0) {
    return (
      <Alert icon={<IconCurrencyDollar size={16} />} color="gray" variant="light">
        {t("admin.monitoring.costBreakdown.noData")}
      </Alert>
    );
  }

  const currency = capabilityUsage[0].currency;
  const totalCost = sum(capabilityUsage.map((e) => parseFloat(e.totalCost)));
  const totals = {
    events: sum(capabilityUsage.map((e) => e.eventCount)),
    input: sum(capabilityUsage.map((e) => e.inputTokens)),
    output: sum(capabilityUsage.map((e) => e.outputTokens)),
    tokens: sum(capabilityUsage.map((e) => e.totalTokens)),
    pages: sum(capabilityUsage.map((e) => e.pagesProcessed)),
  };

  const columns = [
    col.badge<ExternalRefCapabilityUsageViewModel>("capability", t("admin.monitoring.costBreakdown.eventType"),
      (item) => <Badge variant="light" size="sm">{item.capability}</Badge>),
    col.number<ExternalRefCapabilityUsageViewModel>("events", t("admin.monitoring.costBreakdown.events"), (i) => i.eventCount),
    col.number<ExternalRefCapabilityUsageViewModel>("input", t("admin.monitoring.costBreakdown.inputTokens"), (i) => i.inputTokens),
    col.number<ExternalRefCapabilityUsageViewModel>("output", t("admin.monitoring.costBreakdown.outputTokens"), (i) => i.outputTokens),
    col.number<ExternalRefCapabilityUsageViewModel>("tokens", t("admin.monitoring.costBreakdown.totalTokens"), (i) => i.totalTokens),
    col.number<ExternalRefCapabilityUsageViewModel>("pages", t("admin.monitoring.costBreakdown.pages"), (i) => i.pagesProcessed),
    col.money<ExternalRefCapabilityUsageViewModel>("cost", t("admin.monitoring.costBreakdown.cost"), (item) => ({
      amount: item.totalCost, currency: item.currency,
      note: `${getCostPercent(item.totalCost, totalCost).toFixed(1)}%`,
    })),
  ];

  const total = (value: React.ReactNode): DataTableFooterCell => ({ content: <Text size="sm" fw={600}>{value}</Text>, align: "right" });
  const footer: DataTableFooterCell[] = [
    { content: <Text size="sm" fw={600}>{t("admin.monitoring.costBreakdown.total")}</Text> },
    total(formatCount(totals.events)),
    total(formatCount(totals.input || null)),
    total(formatCount(totals.output || null)),
    total(formatCount(totals.tokens || null)),
    total(formatCount(totals.pages || null)),
    total(formatMoney(totalCost, currency)),
  ];

  const tiles: Array<[string, string]> = [
    [t("admin.monitoring.costBreakdown.cost"), formatMoney(totalCost, currency)],
    [t("admin.monitoring.costBreakdown.events"), formatCount(totals.events)],
    [t("admin.monitoring.costBreakdown.totalTokens"), formatCount(totals.tokens || null)],
    ...(totals.pages > 0 ? [[t("admin.monitoring.costBreakdown.pages"), formatCount(totals.pages)] as [string, string]] : []),
  ];

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: tiles.length }}>
        {tiles.map(([label, value]) => (
          <Card key={label}>
            <Stack gap={spacePx[1]}>
              <Text size="sm" c="dimmed">{label}</Text>
              <Text fw={700} fz="xl">{value}</Text>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
      <AdminTable columns={columns} data={capabilityUsage} getRowKey={(item) => `${item.capability}-${item.currency}`}
                  footer={footer} />
    </Stack>
  );
};
